<script setup lang="ts">
  import Color from '@tiptap/extension-color'
  import Link from '@tiptap/extension-link'
  import TextAlign from '@tiptap/extension-text-align'
  import TextStyle from '@tiptap/extension-text-style'
  import Underline from '@tiptap/extension-underline'
  import StarterKit from '@tiptap/starter-kit'
  import { type Extensions, type JSONContent, EditorContent, useEditor } from '@tiptap/vue-3'

  import {
    CharacterCountWithValidation,
    EDITOR_CONTENT_LENGTH_DEFAULTS,
    FontSize,
    isDeepEqual,
    isSafeLinkUrl,
    useEditorContentValidation,
  } from '@/shared/lib'
  import type { EditorContentMessageKeys } from '@/shared/lib'

  import TheEditorToolbar from './TheEditorToolbar.vue'

  const props = withDefaults(
    defineProps<{
      dataTestid?: string
      extensions?: Extensions
      maxLength?: number
      messageKeys: EditorContentMessageKeys
      minLength?: number
      modelValue: Nullable<JSONContent | string>
      required?: boolean
      toolbarAriaLabelKey: string
    }>(),
    {
      extensions: () => [],
      required: false,
    },
  )

  const emit = defineEmits<{
    (e: 'update:modelValue', value: JSONContent): void
  }>()

  const { modelValue, maxLength, minLength, required, messageKeys } = toRefs(props)
  const { MIN: MIN_LENGTH, MAX: MAX_LENGTH } = EDITOR_CONTENT_LENGTH_DEFAULTS

  const editor = useEditor({
    content: '',
    extensions: [
      StarterKit,
      TextStyle,
      FontSize,
      Color,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: false,
        // блокируем небезопасные схемы при вставке ссылок
        validate: isSafeLinkUrl,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      CharacterCountWithValidation.configure({
        minLength: minLength?.value ?? MIN_LENGTH,
        maxLength: maxLength?.value ?? MAX_LENGTH,
        required: required?.value ?? false,
      }),
      ...props.extensions,
    ],
    onUpdate: () => {
      emit('update:modelValue', editor.value!.getJSON())
      touchContentValidation()
    },
  })

  const {
    contentCounterLabel,
    contentErrorMessage,
    isContentValid,
    isCounterOverLimit,
    isInvalid,
    resetValidation,
    showErrorMessage,
    touchContentValidation,
    validate,
  } = useEditorContentValidation({
    editor,
    minLength,
    maxLength,
    required,
    messageKeys: messageKeys.value,
  })

  onMounted(() => {
    watch(
      modelValue,
      (value) => {
        if (value === null || isDeepEqual(editor.value?.getJSON(), value)) {
          return
        }
        editor.value?.commands.setContent(value, false)
      },
      { immediate: true },
    )
  })

  defineExpose({
    isContentValid,
    resetValidation,
    validate,
  })

  const isColorPickerOpen = ref(false)
</script>

<template>
  <div
    class="flex w-full flex-col"
    :data-testid="dataTestid"
  >
    <p
      v-if="showErrorMessage"
      class="text-error m-0 mb-1 text-xs"
    >
      {{ contentErrorMessage }}
    </p>

    <div
      v-if="editor"
      :class="[
        'flex h-108.5 w-full flex-col overflow-hidden rounded-lg border bg-white',
        isInvalid ? 'border-error' : 'border-border',
      ]"
    >
      <TheEditorToolbar
        v-model:color-picker-open="isColorPickerOpen"
        :editor="editor"
        :toolbar-aria-label-key="toolbarAriaLabelKey"
        :content-counter-label="contentCounterLabel"
        :is-counter-over-limit="isCounterOverLimit"
      />

      <div
        class="min-h-0 flex-1 overflow-y-auto px-5 py-4"
        :class="{ 'pointer-events-none select-none': isColorPickerOpen }"
        :aria-hidden="isColorPickerOpen ? 'true' : undefined"
      >
        <EditorContent
          class="[&_.tiptap]:text-body h-full outline-none [&_.tiptap]:h-full [&_.tiptap]:w-full [&_.tiptap]:outline-none"
          :editor="editor"
        />
      </div>
    </div>
  </div>
</template>
